import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Wallet, AlertTriangle, CalendarClock, Building2,
  CheckCircle, Clock, TrendingDown, Users, LayoutGrid, MessageSquarePlus,
  MessageCircle, Send, ChevronRight, ChevronDown,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import LanguageSwitcher from '../components/LanguageSwitcher';
import AdminFinanceExpenses from './AdminFinanceExpenses';
import AdminFinancePnl from './AdminFinancePnl';
import AdminFinanceAccess from './AdminFinanceAccess';
import { buildReminder } from '../lib/reminder';

const fmt = (n) => `${Number(n || 0).toLocaleString('ru-RU')} ₸`;

const COLUMNS = [
  { key: 'waiting', label: 'Ожидает', color: 'amber', dot: 'bg-amber-400', head: 'border-amber-300 bg-amber-50' },
  { key: 'promised', label: 'Обещал оплатить', color: 'blue', dot: 'bg-blue-500', head: 'border-blue-300 bg-blue-50' },
  { key: 'overdue', label: 'Просрочено', color: 'red', dot: 'bg-red-500', head: 'border-red-300 bg-red-50' },
  { key: 'paid', label: 'Оплачено', color: 'green', dot: 'bg-green-500', head: 'border-green-300 bg-green-50' },
];

const METHOD_LABELS = { cash: 'Наличные', card: 'Карта', kaspi: 'Kaspi', transfer: 'Перевод', online: 'Онлайн' };

function AdminFinance({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('receivables');
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('all');
  const [method, setMethod] = useState('all');
  const [search, setSearch] = useState('');
  const [createdDays, setCreatedDays] = useState('90');
  const [view, setView] = useState('board'); // board | clients

  const [board, setBoard] = useState(null);
  const [clients, setClients] = useState(null);
  const [clientRole, setClientRole] = useState('B2C');  // filter for "По клиентам" view
  const [clientOrdersModal, setClientOrdersModal] = useState(null);  // { client }
  const [entities, setEntities] = useState(null);  // { legal_entities, orphans, total_owed, ... }
  const [expandedEntity, setExpandedEntity] = useState(null);  // BIN of the row currently expanded
  const [pointOrdersModal, setPointOrdersModal] = useState(null);  // { entityName, point }
  const [auditModal, setAuditModal] = useState(null);  // audit response
  const [auditLoading, setAuditLoading] = useState(false);
  const [paymentModal, setPaymentModal] = useState(null);  // { entity } — opens "Записать оплату" dialog
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_date: new Date().toISOString().slice(0,10), method: 'transfer', note: '' });
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState(null); // card in detail dialog
  const [followups, setFollowups] = useState([]);
  const [promiseModal, setPromiseModal] = useState(null); // {card} when setting promised date
  const [promiseDate, setPromiseDate] = useState('');
  const [fuKind, setFuKind] = useState('contact');
  const [fuComment, setFuComment] = useState('');
  const [fuDate, setFuDate] = useState('');
  const [noteDraft, setNoteDraft] = useState('');

  useEffect(() => {
    axios.get(`${API}/finance/branches`, { headers: getAuthHeaders() })
      .then(r => setBranches(r.data.branches || [])).catch(() => {});
  }, []);

  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ branch_id: branchId, method, created_days: createdDays });
      if (search) params.set('search', search);
      const r = await axios.get(`${API}/finance/receivables/board?${params}`, { headers: getAuthHeaders() });
      setBoard(r.data);
    } catch (e) {
      toast.error('Не удалось загрузить дебиторку');
    } finally {
      setLoading(false);
    }
  }, [branchId, method, search, createdDays]);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      // group_b2b_by_bin: same as Telegram mini-app — collapse all Lion Coffee
      // accounts (owner + Блок 6/8/9/12) into a single ИП row via their БИН.
      // Individual manager-accounts are still visible in the "По ИП/ТОО" tab
      // via the per-point breakdown.
      const params = new URLSearchParams({ branch_id: branchId, role: clientRole, group_b2b_by_bin: 'true' });
      const r = await axios.get(`${API}/finance/receivables/by-client?${params}`, { headers: getAuthHeaders() });
      setClients(r.data);
    } catch (e) {
      toast.error('Не удалось загрузить сводку по клиентам');
    } finally {
      setLoading(false);
    }
  }, [branchId, clientRole]);

  const loadEntities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ branch_id: branchId });
      const r = await axios.get(`${API}/finance/receivables/by-legal-entity?${params}`, { headers: getAuthHeaders() });
      setEntities(r.data);
    } catch (e) {
      toast.error('Не удалось загрузить сводку по ИП/ТОО');
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  const runAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const r = await axios.get(`${API}/admin/b2b-audit`, { headers: getAuthHeaders() });
      setAuditModal(r.data);
    } catch (e) {
      toast.error('Не удалось загрузить аудит B2B');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'receivables') return;
    if (view === 'board') loadBoard();
    else if (view === 'clients' || view === 'aging') loadClients();
    else if (view === 'entities') loadEntities();
  }, [activeTab, view, loadBoard, loadClients, loadEntities]);

  const patchCard = async (orderId, payload) => {
    try {
      await axios.patch(`${API}/finance/receivables/${orderId}`, payload, { headers: getAuthHeaders() });
      toast.success('Обновлено');
      loadBoard();
      return true;
    } catch (e) {
      toast.error('Ошибка при обновлении');
      return false;
    }
  };

  const openCard = async (card) => {
    setSelected(card);
    setNoteDraft(card.payment_note || '');
    setFuKind('contact'); setFuComment(''); setFuDate('');
    try {
      const r = await axios.get(`${API}/finance/receivables/${card.id}/followups`, { headers: getAuthHeaders() });
      setFollowups(r.data.followups || []);
    } catch { setFollowups([]); }
  };

  const handleDrop = async (columnKey, card) => {
    if (!card || card.column === columnKey) return;
    if (columnKey === 'overdue') { toast.info('Колонка «Просрочено» заполняется автоматически по дате'); return; }
    if (columnKey === 'paid') { await patchCard(card.id, { status: 'paid' }); return; }
    if (columnKey === 'waiting') { await patchCard(card.id, { status: 'pending', promised_payment_date: null }); return; }
    if (columnKey === 'promised') { setPromiseDate(''); setPromiseModal({ card }); return; }
  };

  const confirmPromise = async () => {
    if (!promiseDate) { toast.error('Выберите дату'); return; }
    const ok = await patchCard(promiseModal.card.id, { status: 'pending', promised_payment_date: promiseDate });
    if (ok) setPromiseModal(null);
  };

  const addFollowup = async () => {
    if (!fuComment && !fuDate) { toast.error('Добавьте комментарий или дату'); return; }
    try {
      await axios.post(`${API}/finance/receivables/${selected.id}/followup`,
        { kind: fuKind, comment: fuComment, promised_date: fuDate || null }, { headers: getAuthHeaders() });
      toast.success('Запись добавлена');
      setFuComment(''); setFuDate('');
      const r = await axios.get(`${API}/finance/receivables/${selected.id}/followups`, { headers: getAuthHeaders() });
      setFollowups(r.data.followups || []);
      loadBoard();
    } catch { toast.error('Ошибка'); }
  };

  const saveNote = async () => {
    const ok = await patchCard(selected.id, { payment_note: noteDraft });
    if (ok) setSelected({ ...selected, payment_note: noteDraft });
  };

  const sendReminder = async (channel) => {
    const { waUrl, tgUrl } = buildReminder(selected);
    const url = channel === 'whatsapp' ? waUrl : tgUrl;
    if (!url) { toast.error('У клиента не указан телефон'); return; }
    window.open(url, '_blank', 'noopener');
    try {
      await axios.post(`${API}/finance/receivables/${selected.id}/followup`,
        { kind: 'contact', comment: `Отправлено напоминание (${channel === 'whatsapp' ? 'WhatsApp' : 'Telegram'})`, promised_date: null },
        { headers: getAuthHeaders() });
      const r = await axios.get(`${API}/finance/receivables/${selected.id}/followups`, { headers: getAuthHeaders() });
      setFollowups(r.data.followups || []);
    } catch { /* opening the link is the primary action */ }
  };

  const s = board?.summary;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-finance-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/${currentLang}/admin`)} className="text-gray-500 hover:text-gray-800 flex items-center gap-1" data-testid="finance-back-btn">
              <ArrowLeft className="w-5 h-5" /> Назад
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-700" /> Финансы</h1>
              <p className="text-xs text-gray-500">Учёт оплат и дебиторки по филиалам</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Branch switcher */}
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger className="w-[160px]" data-testid="finance-branch-select">
                <Building2 className="w-4 h-4 mr-1 text-gray-400" />
                <SelectValue placeholder="Филиал" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все филиалы</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id} data-testid={`branch-opt-${b.code}`}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <LanguageSwitcher />
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-[1600px] mx-auto px-4 flex gap-1 border-t">
          {[
            { key: 'receivables', label: 'Дебиторка / Оплаты', enabled: true },
            { key: 'expenses', label: 'Расходы', enabled: true },
            { key: 'income', label: 'Доходы', enabled: false },
            { key: 'pnl', label: 'P&L по филиалам', enabled: true },
            { key: 'access', label: 'Доступ к мини-апп', enabled: true },
          ].map(tab => (
            <button
              key={tab.key}
              data-testid={`finance-tab-${tab.key}`}
              onClick={() => tab.enabled && setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-800'} ${!tab.enabled && 'opacity-40 cursor-not-allowed'}`}
            >
              {tab.label}{!tab.enabled && <span className="ml-1 text-[10px]">скоро</span>}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 py-5">
        {activeTab === 'expenses' && (
          <AdminFinanceExpenses branchId={branchId} branches={branches} />
        )}
        {activeTab === 'pnl' && (
          <AdminFinancePnl branchId={branchId} branches={branches} />
        )}
        {activeTab === 'access' && (
          <AdminFinanceAccess />
        )}
        {activeTab === 'receivables' && (
          <>
            {/* Summary cards */}
            {s && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                <SummaryCard icon={Wallet} tint="emerald" label="Всего дебиторка" value={fmt(s.total_receivable)} sub={`${board.counts.waiting + board.counts.promised + board.counts.overdue} заказов`} testid="sum-receivable" />
                <SummaryCard icon={AlertTriangle} tint="red" label="Просрочено" value={fmt(s.overdue_amount)} sub={`${board.counts.overdue} заказов`} testid="sum-overdue" />
                <SummaryCard icon={CalendarClock} tint="blue" label="Обещали сегодня" value={fmt(s.promised_today_amount)} sub={`${s.promised_today_count} · за неделю ${s.promised_week_count}`} testid="sum-promised" />
                <SummaryCard icon={TrendingDown} tint="gray" label="Старение долга" value={fmt(s.aging.d30_plus)} sub="долг 30+ дней" testid="sum-aging" />
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                <Input placeholder="Заказ или клиент" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadBoard()} className="pl-8 w-56" data-testid="finance-search" />
              </div>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="w-[150px]" data-testid="finance-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все методы</SelectItem>
                  <SelectItem value="cash">Наличные</SelectItem>
                  <SelectItem value="card">Карта</SelectItem>
                  <SelectItem value="kaspi">Kaspi</SelectItem>
                  <SelectItem value="transfer">Перевод</SelectItem>
                </SelectContent>
              </Select>
              <Select value={createdDays} onValueChange={setCreatedDays}>
                <SelectTrigger className="w-[150px]" data-testid="finance-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">За 30 дней</SelectItem>
                  <SelectItem value="90">За 90 дней</SelectItem>
                  <SelectItem value="180">За 180 дней</SelectItem>
                  <SelectItem value="0">За всё время</SelectItem>
                </SelectContent>
              </Select>
              <div className="ml-auto flex rounded-lg border overflow-hidden">
                <button onClick={() => setView('board')} className={`px-3 py-2 text-sm flex items-center gap-1 ${view === 'board' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600'}`} data-testid="view-board-btn"><LayoutGrid className="w-4 h-4" /> По заказам</button>
                <button onClick={() => setView('aging')} className={`px-3 py-2 text-sm flex items-center gap-1 ${view === 'aging' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600'}`} data-testid="view-aging-btn"><TrendingDown className="w-4 h-4" /> Канбан клиентов</button>
                <button onClick={() => setView('clients')} className={`px-3 py-2 text-sm flex items-center gap-1 ${view === 'clients' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600'}`} data-testid="view-clients-btn"><Users className="w-4 h-4" /> По клиентам</button>
                <button onClick={() => setView('entities')} className={`px-3 py-2 text-sm flex items-center gap-1 ${view === 'entities' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600'}`} data-testid="view-entities-btn"><Building2 className="w-4 h-4" /> По ИП/ТОО</button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-20 text-gray-400" data-testid="finance-loading">Загрузка…</div>
            ) : view === 'board' && board ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="finance-kanban">
                {COLUMNS.map(col => (
                  <div
                    key={col.key}
                    onDragOver={e => e.preventDefault()}
                    onDrop={() => { const c = window.__dragCard; handleDrop(col.key, c); }}
                    className="bg-gray-100/70 rounded-xl p-2 min-h-[200px]"
                    data-testid={`col-${col.key}`}
                  >
                    <div className={`flex items-center justify-between px-2 py-2 rounded-lg border ${col.head} mb-2`}>
                      <span className="flex items-center gap-2 text-sm font-semibold text-gray-800"><span className={`w-2.5 h-2.5 rounded-full ${col.dot}`} /> {col.label}</span>
                      <Badge variant="secondary" data-testid={`count-${col.key}`}>{board.counts[col.key]}</Badge>
                    </div>
                    <div className="space-y-2">
                      {board.columns[col.key].map(card => (
                        <div
                          key={card.id}
                          draggable
                          onDragStart={() => { window.__dragCard = card; }}
                          onClick={() => openCard(card)}
                          className="bg-white rounded-lg border p-3 cursor-pointer hover:shadow-md transition-shadow"
                          data-testid={`card-${card.order_number}`}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <span className="font-medium text-sm text-gray-900 line-clamp-1">{card.customer_name}</span>
                            <span className="font-bold text-sm text-gray-900 whitespace-nowrap">{fmt(card.amount)}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">#{card.order_number} · {METHOD_LABELS[card.payment_method] || card.payment_method}</div>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {card.branch_name && card.branch_name !== '—' && <Badge variant="outline" className="text-[10px]">{card.branch_name}</Badge>}
                            {card.promised_payment_date && <span className="text-[11px] text-blue-600 flex items-center gap-1"><CalendarClock className="w-3 h-3" />{card.promised_payment_date}</span>}
                            {col.key !== 'paid' && <span className="text-[11px] text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{card.days_outstanding}д</span>}
                          </div>
                          {col.key !== 'paid' && (
                            <Button size="sm" variant="outline" className="w-full mt-2 h-7 text-xs text-green-700 border-green-300 hover:bg-green-50"
                              onClick={(e) => { e.stopPropagation(); patchCard(card.id, { status: 'paid' }); }}
                              data-testid={`mark-paid-${card.order_number}`}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Оплачено
                            </Button>
                          )}
                        </div>
                      ))}
                      {board.has_more?.[col.key] && <div className="text-center text-[11px] text-gray-400 py-1">показаны первые 80 · уточните фильтр</div>}
                      {board.columns[col.key].length === 0 && <div className="text-center text-xs text-gray-400 py-6">пусто</div>}
                    </div>
                  </div>
                ))}
              </div>
            ) : view === 'aging' && clients ? (
              (() => {
                // AR Aging: bucket clients by the age of their oldest unpaid order.
                // (These are the classic 4 buckets used in accounting.)
                const BUCKETS = [
                  { id: 'fresh',     label: 'Свежий долг',   sub: '0–7 дней',   color: 'emerald', maxDays: 7 },
                  { id: 'attention', label: 'Внимание',      sub: '8–30 дней',  color: 'yellow',  maxDays: 30 },
                  { id: 'overdue',   label: 'Просрочка',     sub: '31–60 дней', color: 'orange',  maxDays: 60 },
                  { id: 'critical',  label: 'Критично',      sub: '60+ дней',   color: 'red',     maxDays: Infinity },
                ];
                const columns = BUCKETS.map(b => ({ ...b, cards: [] }));
                // Show the REMAINING balance (after ledger payments), not the raw
                // unpaid-orders total — so a client who paid part of their debt shows
                // the real outstanding amount right on the card.
                const bal = (c) => (c.balance != null ? c.balance : c.total_owed);
                clients.clients.forEach(c => {
                  const col = columns.find(b => c.oldest_days <= b.maxDays) || columns[columns.length - 1];
                  col.cards.push(c);
                });
                columns.forEach(col => col.cards.sort((a, b) => bal(b) - bal(a)));
                const totalDebt = clients.total_balance != null ? clients.total_balance : clients.total_owed;
                const overdueDebt = columns.slice(2).reduce((s, c) => s + c.cards.reduce((ss, cc) => ss + bal(cc), 0), 0);
                const overduePct = totalDebt > 0 ? Math.round(overdueDebt / totalDebt * 100) : 0;

                const colorClasses = {
                  emerald: { hdr: 'bg-emerald-50 border-emerald-300 text-emerald-900', ring: 'border-emerald-200' },
                  yellow:  { hdr: 'bg-yellow-50 border-yellow-300 text-yellow-900',    ring: 'border-yellow-200' },
                  orange:  { hdr: 'bg-orange-50 border-orange-300 text-orange-900',    ring: 'border-orange-200' },
                  red:     { hdr: 'bg-red-50 border-red-300 text-red-900',             ring: 'border-red-300' },
                };

                return (
                  <div className="space-y-3">
                    {/* Summary bar */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="border rounded-lg p-3 bg-white">
                        <div className="text-xs text-gray-500">Всего клиентов с долгом</div>
                        <div className="text-xl font-bold">{clients.total_clients}</div>
                      </div>
                      <div className="border rounded-lg p-3 bg-white">
                        <div className="text-xs text-gray-500">Общий остаток</div>
                        <div className="text-xl font-bold text-emerald-800">{fmt(totalDebt)}</div>
                      </div>
                      <div className="border rounded-lg p-3 bg-red-50 border-red-200">
                        <div className="text-xs text-red-700">Просрочено (30+ дней)</div>
                        <div className="text-xl font-bold text-red-800">{fmt(overdueDebt)} <span className="text-sm font-normal">({overduePct}%)</span></div>
                      </div>
                      <div className="border rounded-lg p-3 bg-white">
                        <div className="text-xs text-gray-500">Фильтр</div>
                        <div className="flex rounded-md border overflow-hidden text-xs mt-1">
                          {[{id:'B2C',l:'B2C'},{id:'B2B',l:'B2B'},{id:'all',l:'Все'}].map(o => (
                            <button key={o.id} onClick={() => setClientRole(o.id)}
                              className={`flex-1 py-1 ${clientRole === o.id ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                              data-testid={`aging-role-${o.id}`}>{o.l}</button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Kanban columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                      {columns.map((col, ci) => {
                        const colTotal = col.cards.reduce((s, c) => s + bal(c), 0);
                        const cc = colorClasses[col.color];
                        return (
                          <div key={col.id} className={`border rounded-lg overflow-hidden ${cc.ring}`} data-testid={`aging-col-${col.id}`}>
                            <div className={`px-3 py-2 border-b-2 ${cc.hdr}`}>
                              <div className="flex items-baseline justify-between">
                                <div className="font-semibold">{col.label}</div>
                                <div className="text-xs opacity-70">{col.sub}</div>
                              </div>
                              <div className="flex items-baseline justify-between mt-0.5">
                                <div className="text-xs">{col.cards.length} {col.cards.length === 1 ? 'клиент' : 'клиентов'}</div>
                                <div className="font-bold">{fmt(colTotal)}</div>
                              </div>
                            </div>
                            <div className="p-2 space-y-2 min-h-[240px] max-h-[68vh] overflow-y-auto bg-gray-50/50">
                              {col.cards.map((c, i) => (
                                <div key={c.user_id || `${col.id}-${i}`}
                                  className="bg-white border rounded-md p-2.5 shadow-sm hover:shadow-md hover:border-emerald-400 cursor-pointer transition"
                                  onClick={() => setClientOrdersModal({ client: c })}
                                  data-testid={`aging-card-${col.id}-${i}`}>
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="font-semibold text-sm truncate">{c.customer_name}</div>
                                    <div className={`text-sm font-bold whitespace-nowrap ${bal(c) <= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(bal(c))}</div>
                                  </div>
                                  {c.paid_ledger_total > 0 && (
                                    <div className="text-[10px] text-gray-500 mt-0.5">
                                      оплачено <span className="text-blue-600 font-medium">{fmt(c.paid_ledger_total)}</span> из {fmt(c.total_owed)}
                                    </div>
                                  )}
                                  {c.customer_email && <div className="text-[11px] text-gray-500 truncate">{c.customer_email}</div>}
                                  <div className="flex items-center justify-between mt-1.5 text-[11px] text-gray-500">
                                    <div>
                                      <Badge className="bg-gray-100 text-gray-600 text-[9px] mr-1">{c.customer_type}</Badge>
                                      {c.orders_count} заказов
                                    </div>
                                    <div className={c.oldest_days > 60 ? 'text-red-600 font-semibold' : c.oldest_days > 30 ? 'text-orange-600' : ''}>
                                      старейший {c.oldest_days}д
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {col.cards.length === 0 && (
                                <div className="text-center text-gray-400 text-xs py-8">пусто</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
            ) : view === 'clients' && clients ? (
              <Card>
                <CardContent className="p-0">
                  <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap items-center gap-3 text-sm">
                    <span className="font-semibold">Всего клиентов с долгом: {clients.total_clients}</span>
                    <span>Начислено: <b className="text-gray-800">{fmt(clients.total_owed)}</b></span>
                    <span>Остаток: <b className="text-emerald-800">{fmt(clients.total_balance != null ? clients.total_balance : clients.total_owed)}</b></span>
                    <div className="ml-auto flex rounded-md border overflow-hidden text-xs">
                      {[
                        { id: 'B2C', label: 'B2C' },
                        { id: 'B2B', label: 'B2B' },
                        { id: 'all', label: 'Все' },
                      ].map(opt => (
                        <button key={opt.id}
                          onClick={() => setClientRole(opt.id)}
                          className={`px-3 py-1.5 ${clientRole === opt.id ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                          data-testid={`clients-role-${opt.id}`}>{opt.label}</button>
                      ))}
                    </div>
                  </div>
                  <table className="w-full text-sm" data-testid="clients-table">
                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                      <tr>
                        <th className="text-left px-4 py-2">Клиент</th>
                        <th className="text-left px-4 py-2">Контакт</th>
                        <th className="text-left px-4 py-2">Тип</th>
                        <th className="text-right px-4 py-2">Заказов</th>
                        <th className="text-right px-4 py-2">Начислено</th>
                        <th className="text-right px-4 py-2">Оплачено</th>
                        <th className="text-right px-4 py-2">Остаток</th>
                        <th className="text-left px-4 py-2">Обещанная дата</th>
                        <th className="text-left px-4 py-2">Старейший</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.clients.map((c, i) => (
                        <tr key={c.user_id || i}
                          className="border-t hover:bg-blue-50 cursor-pointer"
                          onClick={() => setClientOrdersModal({ client: c })}
                          data-testid={`client-row-${i}`}>
                          <td className="px-4 py-2 font-medium underline decoration-dotted">
                            {c.customer_name}
                            {c.overdue && <Badge className="ml-2 bg-red-100 text-red-700 text-[10px]">просрочка</Badge>}
                          </td>
                          <td className="px-4 py-2 text-gray-500 text-xs">
                            {c.customer_email && <div className="truncate max-w-[220px]">{c.customer_email}</div>}
                            {c.customer_phone && <div>{c.customer_phone}</div>}
                          </td>
                          <td className="px-4 py-2 text-gray-500">{c.customer_type}</td>
                          <td className="px-4 py-2 text-right">{c.orders_count}</td>
                          <td className="px-4 py-2 text-right text-gray-700">{fmt(c.total_owed)}</td>
                          <td className="px-4 py-2 text-right text-blue-700">{fmt(c.paid_ledger_total || 0)}</td>
                          <td className={`px-4 py-2 text-right font-bold ${(c.balance != null ? c.balance : c.total_owed) <= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(c.balance != null ? c.balance : c.total_owed)}</td>
                          <td className="px-4 py-2 text-blue-600">{c.nearest_promised_date || '—'}</td>
                          <td className="px-4 py-2 text-gray-500">{c.oldest_days}д</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {clients.clients.length === 0 && <div className="text-center text-gray-400 py-10">Нет должников</div>}
                </CardContent>
              </Card>
            ) : view === 'entities' && entities ? (
              <Card>
                <CardContent className="p-0">
                  <div className="px-4 py-3 border-b bg-emerald-50 flex flex-wrap items-center gap-4 text-sm">
                    <span className="font-semibold text-emerald-900">Всего ИП/ТОО с долгом: {entities.total_legal_entities}</span>
                    <span className="text-emerald-800">Общая сумма: <b>{fmt(entities.total_owed)}</b></span>
                    {entities.orphan_total > 0 && (
                      <span className="text-amber-700" title="B2B без корректного БИН — требует проверки">
                        ⚠️ Без БИН: {entities.orphans.length} заказов, {fmt(entities.orphan_total)}
                      </span>
                    )}
                    <Button size="sm" variant="outline" className="ml-auto text-xs h-8"
                      onClick={runAudit} disabled={auditLoading}
                      data-testid="run-b2b-audit-btn">
                      <Search className="w-3.5 h-3.5 mr-1" />
                      {auditLoading ? 'Проверка…' : 'Аудит B2B данных'}
                    </Button>
                  </div>
                  <table className="w-full text-sm" data-testid="entities-table">
                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                      <tr>
                        <th className="text-left px-4 py-2 w-8"></th>
                        <th className="text-left px-4 py-2">ИП / ТОО</th>
                        <th className="text-left px-4 py-2">БИН</th>
                        <th className="text-right px-4 py-2">Начислено</th>
                        <th className="text-right px-4 py-2">Оплачено</th>
                        <th className="text-right px-4 py-2">Остаток</th>
                        <th className="text-right px-4 py-2">Заказов</th>
                        <th className="text-left px-4 py-2">Действие</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entities.legal_entities.map((ent, i) => (
                        <React.Fragment key={ent.key}>
                          <tr className={`border-t hover:bg-gray-50 cursor-pointer ${expandedEntity === ent.key ? 'bg-emerald-50/60' : ''}`}
                            onClick={() => setExpandedEntity(expandedEntity === ent.key ? null : ent.key)}
                            data-testid={`entity-row-${i}`}>
                            <td className="px-3 py-2 text-gray-400">
                              {expandedEntity === ent.key
                                ? <ChevronDown className="w-4 h-4" />
                                : <ChevronRight className="w-4 h-4" />}
                            </td>
                            <td className="px-4 py-2 font-medium">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-emerald-600" />
                                {ent.legal_name}
                                {ent.overdue && <Badge className="bg-red-100 text-red-700 text-[10px]">просрочка {ent.overdue_count}</Badge>}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-gray-500 font-mono text-xs">{ent.bin_iin}</td>
                            <td className="px-4 py-2 text-right text-gray-700">{fmt(ent.accrued)}</td>
                            <td className="px-4 py-2 text-right text-blue-700">{fmt(ent.paid_total)}
                              {ent.paid_ledger_total > 0 && <div className="text-[10px] text-gray-500">акт: {fmt(ent.paid_ledger_total)}</div>}
                            </td>
                            <td className={`px-4 py-2 text-right font-bold ${ent.balance <= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                              {fmt(ent.balance)}
                            </td>
                            <td className="px-4 py-2 text-right text-gray-700">{ent.orders_count}</td>
                            <td className="px-4 py-2">
                              <Button size="sm" variant="outline" className="h-7 text-[11px] text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                onClick={(e) => { e.stopPropagation(); setPaymentModal({ entity: ent });
                                  setPaymentForm({ amount: '', payment_date: new Date().toISOString().slice(0,10), method: 'transfer', note: '' }); }}
                                data-testid={`entity-record-payment-${i}`}>
                                <Wallet className="w-3 h-3 mr-1" /> Записать оплату
                              </Button>
                            </td>
                          </tr>
                          {expandedEntity === ent.key && (
                            <>
                              {ent.points.map((p, j) => (
                                <tr key={`${ent.key}-${p.point_key}`}
                                  className="border-t bg-gray-50/50 text-xs cursor-pointer hover:bg-blue-50"
                                  onClick={(e) => { e.stopPropagation(); setPointOrdersModal({ entity: ent, point: p }); }}
                                  data-testid={`entity-${i}-point-${j}`}>
                                  <td></td>
                                  <td className="px-4 py-1.5 pl-10 text-gray-700 underline decoration-dotted">└─ {p.point_label}</td>
                                  <td></td>
                                  <td className="px-4 py-1.5 text-right text-gray-500">{fmt(p.owed)}</td>
                                  <td></td>
                                  <td></td>
                                  <td className="px-4 py-1.5 text-right text-gray-500">{p.orders_count}</td>
                                  <td className="px-4 py-1.5 text-gray-500">{p.oldest_days}д {p.overdue && <span className="text-red-600">просрочка</span>}</td>
                                </tr>
                              ))}
                              {ent.payments && ent.payments.length > 0 && (
                                <tr className="border-t bg-blue-50/40 text-xs" data-testid={`entity-${i}-payments-header`}>
                                  <td></td>
                                  <td colSpan="7" className="px-4 py-1.5 pl-10 text-blue-900 font-semibold">💰 История оплат ({ent.payments.length})</td>
                                </tr>
                              )}
                              {ent.payments && ent.payments.map((p, j) => (
                                <tr key={`pay-${p.id}`} className="border-t bg-blue-50/20 text-xs" data-testid={`entity-${i}-payment-${j}`}>
                                  <td></td>
                                  <td className="px-4 py-1.5 pl-10 text-blue-800">└─ {p.payment_date} · {METHOD_LABELS[p.method] || p.method}
                                    {p.note && <span className="text-gray-500 ml-2 italic">«{p.note}»</span>}
                                  </td>
                                  <td></td>
                                  <td></td>
                                  <td className="px-4 py-1.5 text-right text-blue-700 font-semibold">{fmt(p.amount)}</td>
                                  <td></td>
                                  <td className="px-4 py-1.5 text-gray-500">{p.created_by_name || '—'}</td>
                                  <td className="px-4 py-1.5 text-right">
                                    <button
                                      className="text-red-600 hover:text-red-800 text-[10px]"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (!window.confirm('Удалить эту запись оплаты? Автозакрытые заказы вернутся в статус «не оплачен».')) return;
                                        try {
                                          const r = await axios.delete(`${API}/finance/client-payments/${p.id}`, { headers: getAuthHeaders() });
                                          const msg = r.data.rolled_back_orders ? `Оплата удалена, ${r.data.rolled_back_orders} заказов возвращено` : 'Оплата удалена';
                                          toast.success(msg);
                                          await loadEntities();
                                        } catch {
                                          toast.error('Не удалось удалить');
                                        }
                                      }}
                                      data-testid={`entity-${i}-payment-delete-${j}`}>
                                      удалить
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  {entities.legal_entities.length === 0 && <div className="text-center text-gray-400 py-10">Нет должников по B2B</div>}
                </CardContent>
              </Card>
            ) : null}
          </>
        )}
      </main>

      {/* Client orders modal (drill-down for "По клиентам" view) */}
      <Dialog open={!!clientOrdersModal} onOpenChange={(o) => !o && setClientOrdersModal(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="client-orders-modal">
          {clientOrdersModal && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between pr-6">
                  <span>{clientOrdersModal.client.customer_name}</span>
                  <span className={clientOrdersModal.client.balance <= 0 ? 'text-emerald-700' : 'text-red-700'}>{fmt(clientOrdersModal.client.balance ?? clientOrdersModal.client.total_owed)}</span>
                </DialogTitle>
                <DialogDescription className="text-xs text-gray-500">
                  {clientOrdersModal.client.customer_type} ·{' '}
                  {clientOrdersModal.client.customer_email || clientOrdersModal.client.customer_phone || '—'} ·{' '}
                  {clientOrdersModal.client.orders_count} неоплаченных заказов
                  {clientOrdersModal.client.overdue && <span className="ml-2 text-red-600 font-semibold">· ⚠️ есть просроченные</span>}
                </DialogDescription>
              </DialogHeader>

              {/* Balance breakdown + Record payment button */}
              <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-2 text-xs">
                <div>
                  <div className="text-gray-500">Долг по заказам</div>
                  <div className="font-semibold">{fmt(clientOrdersModal.client.total_owed)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Оплачено (акт)</div>
                  <div className="font-semibold text-blue-700">{fmt(clientOrdersModal.client.paid_ledger_total || 0)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Остаток</div>
                  <div className={`font-bold ${(clientOrdersModal.client.balance || 0) <= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {fmt(clientOrdersModal.client.balance ?? clientOrdersModal.client.total_owed)}
                  </div>
                </div>
                <Button size="sm" className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                  onClick={() => {
                    // Reuse the same payment modal — pretend client is an "entity" for both B2B and B2C.
                    setPaymentModal({ entity: {
                      legal_name: clientOrdersModal.client.customer_name,
                      bin_iin: clientOrdersModal.client.bin_iin || null,
                      user_id: clientOrdersModal.client.user_id || null,
                      balance: clientOrdersModal.client.balance ?? clientOrdersModal.client.total_owed,
                    }});
                    setPaymentForm({ amount: '', payment_date: new Date().toISOString().slice(0,10), method: 'transfer', note: '' });
                  }}
                  data-testid="client-record-payment">
                  <Wallet className="w-3 h-3 mr-1" /> Записать оплату
                </Button>
              </div>

              {clientOrdersModal.client.payments && clientOrdersModal.client.payments.length > 0 && (
                <div className="border-t pt-2">
                  <div className="text-xs font-semibold text-blue-900 mb-1">💰 История оплат ({clientOrdersModal.client.payments.length})</div>
                  <div className="space-y-1 text-[11px]">
                    {clientOrdersModal.client.payments.map(p => (
                      <div key={p.id} className="flex items-center justify-between border rounded px-2 py-1 bg-blue-50/40" data-testid={`client-payment-${p.id}`}>
                        <div>
                          <span className="font-semibold text-blue-800">{fmt(p.amount)}</span>
                          <span className="text-gray-500 ml-2">{p.payment_date} · {METHOD_LABELS[p.method] || p.method}</span>
                          {p.note && <span className="text-gray-400 italic ml-2">«{p.note}»</span>}
                        </div>
                        <button className="text-red-600 text-[10px] hover:text-red-800"
                          onClick={async () => {
                            if (!window.confirm('Удалить эту запись оплаты?')) return;
                            try {
                              const r = await axios.delete(`${API}/finance/client-payments/${p.id}`, { headers: getAuthHeaders() });
                              toast.success(r.data.rolled_back_orders ? `Удалено, ${r.data.rolled_back_orders} заказов возвращено` : 'Удалено');
                              await loadClients();
                              setClientOrdersModal(null);
                            } catch { toast.error('Не удалось удалить'); }
                          }}
                          data-testid={`client-payment-delete-${p.id}`}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">№ заказа</th>
                    <th className="text-left px-2 py-1.5">Создан</th>
                    <th className="text-left px-2 py-1.5">Доставка</th>
                    <th className="text-right px-2 py-1.5">Сумма</th>
                    <th className="text-left px-2 py-1.5">Метод</th>
                    <th className="text-left px-2 py-1.5">Обещали</th>
                    <th className="text-right px-2 py-1.5 w-24">Действие</th>
                  </tr>
                </thead>
                <tbody>
                  {clientOrdersModal.client.orders.map((o, i) => (
                    <tr key={o.id} className={`border-t ${o.overdue ? 'bg-red-50/40' : ''}`} data-testid={`client-order-${i}`}>
                      <td className="px-2 py-1.5 font-mono">#{o.order_number}</td>
                      <td className="px-2 py-1.5 text-gray-500">{o.created_at ? new Date(o.created_at).toLocaleDateString('ru-RU') : '—'} <span className="text-gray-400">({o.days_old}д)</span></td>
                      <td className="px-2 py-1.5 text-gray-500">{o.delivery_date || '—'}</td>
                      <td className="px-2 py-1.5 text-right font-semibold">{fmt(o.amount)}</td>
                      <td className="px-2 py-1.5 text-gray-500">{METHOD_LABELS[o.payment_method] || o.payment_method || '—'}</td>
                      <td className="px-2 py-1.5 text-blue-600">{o.promised_payment_date || '—'}</td>
                      <td className="px-2 py-1.5 text-right">
                        <Button size="sm" variant="outline" className="h-6 text-[10px] text-green-700 border-green-300 hover:bg-green-50"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await axios.patch(`${API}/finance/receivables/${o.id}`, { status: 'paid' }, { headers: getAuthHeaders() });
                              toast.success(`Заказ #${o.order_number} отмечен как оплаченный`);
                              await loadClients();
                              setClientOrdersModal(null);
                            } catch (err) {
                              toast.error('Не удалось обновить статус');
                            }
                          }}
                          data-testid={`client-order-paid-${o.order_number}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Оплачен
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {clientOrdersModal.client.orders.length === 0 && (
                    <tr><td colSpan="7" className="text-center text-gray-400 py-6">Нет неоплаченных заказов</td></tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Record payment dialog (акт сверки) */}
      <Dialog open={!!paymentModal} onOpenChange={(o) => !o && setPaymentModal(null)}>
        <DialogContent className="max-w-md" data-testid="record-payment-modal">
          {paymentModal && (
            <>
              <DialogHeader>
                <DialogTitle>Записать оплату</DialogTitle>
                <DialogDescription className="text-xs">
                  {paymentModal.entity.legal_name}
                  {paymentModal.entity.bin_iin && <> · БИН {paymentModal.entity.bin_iin}</>}
                  {!paymentModal.entity.bin_iin && paymentModal.entity.user_id && <> · B2C</>}
                </DialogDescription>
                <div className="text-xs text-gray-700">
                  Текущий остаток: <b className="text-red-700">{fmt(paymentModal.entity.balance)}</b>
                </div>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Сумма, ₸</label>
                  <input type="number" min="1" step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="Например 5000000"
                    data-testid="payment-amount-input" autoFocus />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Дата оплаты</label>
                  <input type="date" value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                    className="w-full border rounded px-3 py-2 text-sm"
                    data-testid="payment-date-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Способ</label>
                  <select value={paymentForm.method}
                    onChange={(e) => setPaymentForm({...paymentForm, method: e.target.value})}
                    className="w-full border rounded px-3 py-2 text-sm"
                    data-testid="payment-method-input">
                    <option value="transfer">Банковский перевод</option>
                    <option value="cash">Наличные</option>
                    <option value="kaspi">Kaspi</option>
                    <option value="card">Карта</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Заметка (необязательно)</label>
                  <input type="text" value={paymentForm.note}
                    onChange={(e) => setPaymentForm({...paymentForm, note: e.target.value})}
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="Например: аванс за июль"
                    data-testid="payment-note-input" />
                </div>
                {paymentForm.amount && paymentModal.entity.balance > 0 && (
                  <div className="bg-gray-50 rounded p-2 text-xs">
                    После оплаты остаток будет:{' '}
                    <b className={paymentModal.entity.balance - Number(paymentForm.amount) <= 0 ? 'text-emerald-700' : 'text-red-700'}>
                      {fmt(paymentModal.entity.balance - Number(paymentForm.amount))}
                    </b>
                    {paymentModal.entity.balance - Number(paymentForm.amount) <= 0 && (
                      <div className="mt-1 text-emerald-700">
                        ⚠️ Все неоплаченные заказы этого ИП будут автоматически закрыты
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setPaymentModal(null)}
                  data-testid="payment-cancel">Отмена</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={paymentSaving || !paymentForm.amount || Number(paymentForm.amount) <= 0}
                  onClick={async () => {
                    setPaymentSaving(true);
                    try {
                      const r = await axios.post(`${API}/finance/client-payments`, {
                        bin_iin: paymentModal.entity.bin_iin || null,
                        user_id: paymentModal.entity.user_id || null,
                        amount: Number(paymentForm.amount),
                        payment_date: paymentForm.payment_date,
                        method: paymentForm.method,
                        note: paymentForm.note || null,
                      }, { headers: getAuthHeaders() });
                      const closed = r.data.auto_closed_count || 0;
                      toast.success(closed ? `Оплата записана, автозакрыто ${closed} заказов` : 'Оплата записана');
                      setPaymentModal(null);
                      // Refresh whichever view is open
                      await Promise.all([loadEntities(), loadClients()]);
                      setClientOrdersModal(null);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Ошибка при сохранении');
                    } finally {
                      setPaymentSaving(false);
                    }
                  }}
                  data-testid="payment-save">
                  {paymentSaving ? 'Сохранение…' : 'Записать'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Point orders modal (list of unpaid orders for one point inside an ИП) */}
      <Dialog open={!!pointOrdersModal} onOpenChange={(o) => !o && setPointOrdersModal(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="point-orders-modal">
          {pointOrdersModal && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between pr-6">
                  <span>{pointOrdersModal.entity.legal_name} — {pointOrdersModal.point.point_label}</span>
                  <span className="text-emerald-700">{fmt(pointOrdersModal.point.owed)}</span>
                </DialogTitle>
                <DialogDescription>
                  {pointOrdersModal.point.orders_count} неоплаченных заказов
                  {pointOrdersModal.point.overdue && <span className="ml-2 text-red-600 font-semibold">· ⚠️ есть просроченные</span>}
                </DialogDescription>
              </DialogHeader>
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">№ заказа</th>
                    <th className="text-left px-2 py-1.5">Создан</th>
                    <th className="text-left px-2 py-1.5">Доставка</th>
                    <th className="text-right px-2 py-1.5">Сумма</th>
                    <th className="text-left px-2 py-1.5">Метод</th>
                    <th className="text-left px-2 py-1.5">Обещали</th>
                    <th className="text-right px-2 py-1.5 w-24">Действие</th>
                  </tr>
                </thead>
                <tbody>
                  {pointOrdersModal.point.orders.map((o, i) => (
                    <tr key={o.id} className={`border-t ${o.overdue ? 'bg-red-50/40' : ''}`} data-testid={`point-order-${i}`}>
                      <td className="px-2 py-1.5 font-mono">#{o.order_number}</td>
                      <td className="px-2 py-1.5 text-gray-500">{o.created_at ? new Date(o.created_at).toLocaleDateString('ru-RU') : '—'} <span className="text-gray-400">({o.days_old}д)</span></td>
                      <td className="px-2 py-1.5 text-gray-500">{o.delivery_date || '—'}</td>
                      <td className="px-2 py-1.5 text-right font-semibold">{fmt(o.amount)}</td>
                      <td className="px-2 py-1.5 text-gray-500">{METHOD_LABELS[o.payment_method] || o.payment_method || '—'}</td>
                      <td className="px-2 py-1.5 text-blue-600">{o.promised_payment_date || '—'}</td>
                      <td className="px-2 py-1.5 text-right">
                        <Button size="sm" variant="outline" className="h-6 text-[10px] text-green-700 border-green-300 hover:bg-green-50"
                          onClick={async () => {
                            try {
                              await axios.patch(`${API}/finance/receivables/${o.id}`, { status: 'paid' }, { headers: getAuthHeaders() });
                              toast.success(`Заказ #${o.order_number} отмечен как оплаченный`);
                              // Reload the parent entities list to refresh totals
                              await loadEntities();
                              setPointOrdersModal(null);
                            } catch (err) {
                              toast.error('Не удалось обновить статус');
                            }
                          }}
                          data-testid={`point-order-paid-${o.order_number}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Оплачен
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {pointOrdersModal.point.orders.length === 0 && (
                    <tr><td colSpan="7" className="text-center text-gray-400 py-6">Нет неоплаченных заказов</td></tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* B2B data audit modal */}
      <Dialog open={!!auditModal} onOpenChange={(o) => !o && setAuditModal(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="b2b-audit-modal">
          {auditModal && (
            <>
              <DialogHeader>
                <DialogTitle>Аудит B2B данных</DialogTitle>
                <DialogDescription>
                  Проверено {auditModal.summary.total_active_b2b} активных B2B-клиентов
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-5 gap-3 mb-4">
                <div className="border rounded-lg p-3 bg-red-50 border-red-200">
                  <div className="text-2xl font-bold text-red-800">{auditModal.summary.missing_bin}</div>
                  <div className="text-xs text-red-700">без БИН / некорректный</div>
                </div>
                <div className="border rounded-lg p-3 bg-amber-50 border-amber-200">
                  <div className="text-2xl font-bold text-amber-800">{auditModal.summary.name_mismatch}</div>
                  <div className="text-xs text-amber-700">имя ≠ сеть</div>
                </div>
                <div className="border rounded-lg p-3 bg-blue-50 border-blue-200">
                  <div className="text-2xl font-bold text-blue-800">{auditModal.summary.duplicate_bin}</div>
                  <div className="text-xs text-blue-700">дубли БИН</div>
                </div>
                <div className="border rounded-lg p-3 bg-gray-50 border-gray-200">
                  <div className="text-2xl font-bold text-gray-700">{auditModal.summary.orphaned_children}</div>
                  <div className="text-xs text-gray-600">осиротевшие</div>
                </div>
                <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200"
                     title="Дочерние точки без своего БИН, но наследуют корректный БИН от владельца — это НЕ ошибка">
                  <div className="text-2xl font-bold text-emerald-700">{auditModal.summary.inherits_bin || 0}</div>
                  <div className="text-xs text-emerald-700">наследуют БИН</div>
                </div>
              </div>

              {auditModal.missing_bin.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-red-900 mb-1">❌ B2B без корректного БИН ({auditModal.missing_bin.length})</h4>
                  <div className="bg-white border rounded max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0"><tr>
                        <th className="text-left px-2 py-1">Название</th>
                        <th className="text-left px-2 py-1">Email</th>
                        <th className="text-left px-2 py-1">БИН сейчас</th>
                        <th className="text-left px-2 py-1">Сеть</th>
                      </tr></thead>
                      <tbody>
                        {auditModal.missing_bin.map(m => (
                          <tr key={m.user_id} className="border-t">
                            <td className="px-2 py-1">{m.company_name}</td>
                            <td className="px-2 py-1 text-gray-500 truncate max-w-[220px]">{m.email}</td>
                            <td className="px-2 py-1 text-red-600 font-mono">{m.current_bin || '(пусто)'}</td>
                            <td className="px-2 py-1 text-gray-500">{m.network_name || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {auditModal.name_mismatch.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-amber-900 mb-1">⚠️ Имя менеджера ≠ имя сети ({auditModal.name_mismatch.length})</h4>
                  <div className="bg-white border rounded max-h-40 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0"><tr>
                        <th className="text-left px-2 py-1">Email</th>
                        <th className="text-left px-2 py-1">company_name</th>
                        <th className="text-left px-2 py-1">network.name</th>
                      </tr></thead>
                      <tbody>
                        {auditModal.name_mismatch.map(m => (
                          <tr key={m.user_id} className="border-t">
                            <td className="px-2 py-1 text-gray-500 truncate max-w-[220px]">{m.email}</td>
                            <td className="px-2 py-1 text-amber-800">{m.company_name}</td>
                            <td className="px-2 py-1 text-emerald-700">{m.network_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {auditModal.duplicate_bin.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-blue-900 mb-1">🔁 Дубли БИН ({auditModal.duplicate_bin.length})</h4>
                  <div className="bg-white border rounded p-2 space-y-2">
                    {auditModal.duplicate_bin.map(d => (
                      <div key={d.bin_iin} className="text-xs">
                        <div className="font-mono text-blue-700">БИН {d.bin_iin}</div>
                        <ul className="list-disc pl-6 text-gray-700">
                          {d.owners.map(o => (
                            <li key={o.user_id}>{o.company_name} — <span className="text-gray-500">{o.email}</span></li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {auditModal.orphaned_children.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-gray-900 mb-1">👻 Осиротевшие дочерние аккаунты ({auditModal.orphaned_children.length})</h4>
                  <div className="bg-white border rounded max-h-40 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0"><tr>
                        <th className="text-left px-2 py-1">company_name</th>
                        <th className="text-left px-2 py-1">Email</th>
                        <th className="text-left px-2 py-1">Missing parent</th>
                      </tr></thead>
                      <tbody>
                        {auditModal.orphaned_children.map(m => (
                          <tr key={m.user_id} className="border-t">
                            <td className="px-2 py-1">{m.company_name}</td>
                            <td className="px-2 py-1 text-gray-500 truncate max-w-[220px]">{m.email}</td>
                            <td className="px-2 py-1 font-mono text-red-600">{m.missing_parent_id}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {auditModal.inherits_bin && auditModal.inherits_bin.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-emerald-800 mb-1">✅ Наследуют БИН от владельца ({auditModal.inherits_bin.length}) — <span className="font-normal text-gray-500">не ошибка, для информации</span></h4>
                  <div className="bg-white border rounded max-h-32 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0"><tr>
                        <th className="text-left px-2 py-1">Точка</th>
                        <th className="text-left px-2 py-1">Наследуемый БИН</th>
                      </tr></thead>
                      <tbody>
                        {auditModal.inherits_bin.map(m => (
                          <tr key={m.user_id} className="border-t">
                            <td className="px-2 py-1">{m.company_name}</td>
                            <td className="px-2 py-1 font-mono text-emerald-700">{m.parent_bin}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {auditModal.summary.missing_bin === 0 && auditModal.summary.name_mismatch === 0 &&
                auditModal.summary.duplicate_bin === 0 && auditModal.summary.orphaned_children === 0 && (
                <div className="text-center py-8 text-emerald-700">
                  <CheckCircle className="w-10 h-10 mx-auto mb-2" />
                  <div className="font-semibold">Все B2B-данные в порядке!</div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Promise date modal */}
      <Dialog open={!!promiseModal} onOpenChange={(o) => !o && setPromiseModal(null)}>
        <DialogContent className="max-w-sm" data-testid="promise-modal">
          <DialogHeader><DialogTitle>Обещанная дата оплаты</DialogTitle>
          <DialogDescription>Укажите день, когда клиент обещал оплатить заказ.</DialogDescription>
          </DialogHeader>
          <Label className="text-sm">Когда клиент обещал оплатить?</Label>
          <Input type="date" value={promiseDate} onChange={e => setPromiseDate(e.target.value)} data-testid="promise-date-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromiseModal(null)}>Отмена</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={confirmPromise} data-testid="promise-confirm-btn">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Card detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="card-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between pr-6">
                  <span>{selected.customer_name}</span>
                  <span className="text-emerald-700">{fmt(selected.amount)}</span>
                </DialogTitle>
                <p className="text-xs text-gray-500">#{selected.order_number} · {METHOD_LABELS[selected.payment_method] || selected.payment_method} · {selected.branch_name}</p>
              </DialogHeader>
              <DialogDescription className="sr-only">Детали заказа, статус оплаты, заметки и история договорённостей.</DialogDescription>

              <div className="flex gap-2 flex-wrap">
                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => { patchCard(selected.id, { status: 'paid' }); setSelected(null); }} data-testid="detail-mark-paid">
                  <CheckCircle className="w-4 h-4 mr-1" /> Отметить оплаченным
                </Button>
                <Button size="sm" variant="outline" className="text-green-700 border-green-300 hover:bg-green-50 disabled:opacity-40"
                  disabled={!selected.customer_phone} onClick={() => sendReminder('whatsapp')} data-testid="detail-remind-whatsapp"
                  title={selected.customer_phone ? '' : 'У клиента не указан телефон'}>
                  <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                </Button>
                <Button size="sm" variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50"
                  onClick={() => sendReminder('telegram')} data-testid="detail-remind-telegram">
                  <Send className="w-4 h-4 mr-1" /> Telegram
                </Button>
              </div>

              {/* Note */}
              <div>
                <Label className="text-sm">Заметка по оплате</Label>
                <Textarea rows={2} value={noteDraft} onChange={e => setNoteDraft(e.target.value)} data-testid="detail-note" />
                <Button size="sm" variant="outline" className="mt-1" onClick={saveNote} data-testid="detail-save-note">Сохранить заметку</Button>
              </div>

              {/* Add follow-up */}
              <div className="border-t pt-3">
                <Label className="text-sm flex items-center gap-1"><MessageSquarePlus className="w-4 h-4" /> Добавить запись</Label>
                <div className="flex gap-2 mt-1">
                  <Select value={fuKind} onValueChange={setFuKind}>
                    <SelectTrigger className="w-[140px]" data-testid="fu-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contact">Связались</SelectItem>
                      <SelectItem value="promise">Обещание</SelectItem>
                      <SelectItem value="note">Заметка</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="date" value={fuDate} onChange={e => setFuDate(e.target.value)} className="w-[150px]" data-testid="fu-date" />
                </div>
                <Textarea rows={2} className="mt-2" placeholder="Комментарий…" value={fuComment} onChange={e => setFuComment(e.target.value)} data-testid="fu-comment" />
                <Button size="sm" className="mt-1 bg-emerald-600 hover:bg-emerald-700" onClick={addFollowup} data-testid="fu-add-btn">Добавить</Button>
              </div>

              {/* History */}
              <div className="border-t pt-3">
                <Label className="text-sm">История</Label>
                <div className="space-y-2 mt-1" data-testid="fu-history">
                  {followups.length === 0 && <p className="text-xs text-gray-400">Записей пока нет</p>}
                  {followups.map(f => (
                    <div key={f.id} className="text-xs bg-gray-50 rounded p-2">
                      <div className="flex justify-between">
                        <span className="font-medium">{{ contact: 'Связались', promise: 'Обещание', note: 'Заметка', status_change: 'Смена статуса' }[f.kind] || f.kind}</span>
                        <span className="text-gray-400">{f.created_at ? new Date(f.created_at).toLocaleString('ru-RU') : ''}</span>
                      </div>
                      {f.promised_date && <div className="text-blue-600">Обещал: {f.promised_date}</div>}
                      {f.comment && <div className="text-gray-700">{f.comment}</div>}
                      {f.created_by_name && <div className="text-gray-400">— {f.created_by_name}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TINTS = {
  emerald: 'text-emerald-700 bg-emerald-50',
  red: 'text-red-700 bg-red-50',
  blue: 'text-blue-700 bg-blue-50',
  gray: 'text-gray-700 bg-gray-100',
};

function SummaryCard({ icon: Icon, tint, label, value, sub, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4 flex items-start gap-3">
        <div className={`p-2 rounded-lg ${TINTS[tint]}`}><Icon className="w-5 h-5" /></div>
        <div>
          <div className="text-xs text-gray-500">{label}</div>
          <div className="text-xl font-bold text-gray-900">{value}</div>
          <div className="text-[11px] text-gray-400">{sub}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default AdminFinance;
